import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAdmin } from '@/hooks/useAdmin';
import { useAdminSession } from '@/hooks/useAdminSession';

import { useTranslation } from 'react-i18next';

export function AdminGuard({ children }: { children?: React.ReactNode }) {
  const { admin, loading } = useAdmin();
  const { isValid } = useAdminSession();
  const location = useLocation();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#F5F0E8]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-black border-t-transparent" />
          <p className="font-bold text-gray-600">{t('admin.guard.loading')}</p>
        </div>
      </div>
    );
  }

  // First check: Is user an admin in DB?
  if (!admin || !admin.is_active) {
    return <Navigate to="/" replace />;
  }

  // Check for setup mode (first-time password setup)
  const isSetupMode = sessionStorage.getItem('vesper_admin_setup_mode') === 'true';
  const isSettingsPage = location.pathname === '/admin/settings';

  // Allow access to settings page during setup mode
  if (isSetupMode && isSettingsPage) {
    // Clear setup mode after accessing settings
    sessionStorage.removeItem('vesper_admin_setup_mode');
    return children || <Outlet />;
  }

  // Second check: Has valid admin session (password verified)?
  if (!isValid) {
    return <Navigate to="/admin/verify" state={{ from: location }} replace />;
  }

  return children || <Outlet />;
}
